import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { DocumentReference, QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Checkbox, Group, Paper, Stack, Title } from "@mantine/core";

import { IconEdit, IconFile, IconFileText, IconFileX, IconPlus } from "@tabler/icons-react";
import { format } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { ProfileContext } from "@/contexts/profile";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import { fetchUploadedFiles } from "@/file-uploads/services/file-uploads";
import { parseDocumentReferenceFields } from "@/file-uploads/utils/file-uploads";

type FileChange = {
  type: "added" | "modified" | "unchanged";
  file: DocumentReference;
  previousFile?: DocumentReference;
};

const getDocumentTypeDisplay = (file: DocumentReference): string =>
  parseDocumentReferenceFields(file).type || "Unknown";

const hasFileChanged = (current: DocumentReference, previous: DocumentReference): boolean => {
  return (
    current.identifier?.[0]?.value !== previous.identifier?.[0]?.value ||
    current.description !== previous.description ||
    getDocumentTypeDisplay(current) !== getDocumentTypeDisplay(previous) ||
    JSON.stringify(current.securityLabel) !== JSON.stringify(previous.securityLabel)
  );
};

export interface FilesDiffProps {
  /**
   * Legacy index into `fetchAmendmentHistory` (versionId-sorted, amended-only).
   * Prefer passing encounter questionnaire versions when the parent already resolved an amend pair.
   */
  currentVersionIndex?: number;
  currentEncounterQuestionnaireResponse?: QuestionnaireResponse;
  previousEncounterQuestionnaireResponse?: QuestionnaireResponse;
}

export function FilesDiff({
  currentVersionIndex = 0,
  currentEncounterQuestionnaireResponse,
  previousEncounterQuestionnaireResponse,
}: FilesDiffProps) {
  const { encounterId, clientId } = useParams<{ encounterId: string; clientId: string }>();
  const { isPatient } = useContext(ProfileContext);
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [fileChanges, setFileChanges] = useState<FileChange[]>([]);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const encounterVersionPairKey = currentEncounterQuestionnaireResponse?.meta?.versionId
    ? `${currentEncounterQuestionnaireResponse.meta.versionId}:${previousEncounterQuestionnaireResponse?.meta?.versionId ?? ""}`
    : undefined;

  const getVisibilityText = (file: DocumentReference) => {
    const isVisibleToClient = file.securityLabel?.some((label) =>
      label.coding?.some(
        (coding) => coding.code === "forms/documentSecurityLabel/visible-to-client",
      ),
    );
    return isVisibleToClient ? "Visible to Client" : "Not Visible to Client";
  };

  useEffect(() => {
    const fetchFilesAndVersions = async () => {
      if (!clientId || !encounterId) return;

      setIsFetching(true);
      try {
        let current: QuestionnaireResponse | undefined;
        let previous: QuestionnaireResponse | undefined;

        if (currentEncounterQuestionnaireResponse) {
          current = currentEncounterQuestionnaireResponse;
          previous = previousEncounterQuestionnaireResponse;
        } else {
          const encounter = await medplum.readResource("Encounter", encounterId);
          const amendmentHistory = await fetchAmendmentHistory(medplum, encounter);

          if (!amendmentHistory || amendmentHistory.length <= currentVersionIndex) {
            setFileChanges([]);
            return;
          }

          current = amendmentHistory[currentVersionIndex];
          previous = amendmentHistory[currentVersionIndex + 1];
        }

        if (!current) {
          setFileChanges([]);
          return;
        }

        // Get all current files (handle pagination)
        let allCurrentFiles: DocumentReference[] = [];
        let page = 1;
        let hasMore = true;
        const pageSize = 100;

        while (hasMore) {
          const [files] = await fetchUploadedFiles(
            medplum,
            clientId,
            page,
            pageSize,
            {
              fileTypes: [],
            },
            isPatient,
          );
          allCurrentFiles = allCurrentFiles.concat(files);
          if (files.length < pageSize) {
            hasMore = false;
          } else {
            page += 1;
          }
        }

        // Filter files associated with this encounter
        const encounterFiles = allCurrentFiles.filter((file) =>
          file.context?.encounter?.some((encounterReference) =>
            encounterReference.reference?.includes(encounterId),
          ),
        );

        // Get the version dates for comparison
        const currentVersionDate = current.meta?.lastUpdated;
        const previousVersionDate = previous?.meta?.lastUpdated;

        // Compare files to determine changes using DocumentReference history
        const changes: FileChange[] = [];

        // Process each file to get its version history
        for (const file of encounterFiles) {
          if (!file.id) continue;

          try {
            // Get the version history for this specific DocumentReference
            const fileHistory = await medplum.readHistory("DocumentReference", file.id);

            if (!fileHistory.entry || fileHistory.entry.length === 0) {
              // No history available, mark as unchanged
              changes.push({ type: "unchanged", file });
              continue;
            }

            // Get all file versions sorted by date (newest first)
            const allVersions =
              fileHistory.entry
                ?.map((entry) => entry.resource)
                .filter((resource) => resource && resource.meta?.lastUpdated)
                .sort(
                  (firstResource, secondResource) =>
                    new Date(secondResource!.meta!.lastUpdated!).getTime() -
                    new Date(firstResource!.meta!.lastUpdated!).getTime(),
                ) || [];

            // Find the version that was current at the time of the current encounter version
            let currentFileVersion: DocumentReference | undefined;
            if (currentVersionDate) {
              currentFileVersion = allVersions.find(
                (resource) => resource!.meta!.lastUpdated! <= currentVersionDate,
              );
            }

            // Find the version that was current at the time of the previous encounter version
            let previousFileVersion: DocumentReference | undefined;
            if (previousVersionDate) {
              // Find the most recent version that was created before the previous encounter version
              previousFileVersion = allVersions.find(
                (resource) => resource!.meta!.lastUpdated! < previousVersionDate,
              );
            }

            // If we have both versions but they're the same, look for the actual previous different version
            if (
              currentFileVersion &&
              previousFileVersion &&
              currentFileVersion.meta?.lastUpdated === previousFileVersion.meta?.lastUpdated
            ) {
              // Find the next different version in the history
              const currentIndex = allVersions.findIndex(
                (version) => version === currentFileVersion,
              );
              previousFileVersion = allVersions
                .slice(currentIndex + 1)
                .find((version) => version && hasFileChanged(currentFileVersion!, version));
            }

            // Determine the change type
            if (!previousFileVersion && currentFileVersion) {
              // Check if the file was created between the previous and current encounter versions
              const fileCreatedAfterPrevious =
                currentFileVersion.meta?.lastUpdated &&
                previousVersionDate &&
                currentFileVersion.meta.lastUpdated > previousVersionDate;

              if (fileCreatedAfterPrevious) {
                // File was actually added between encounter versions
                changes.push({ type: "added", file });
              } else {
                // File existed before the encounter version, so it's unchanged
                changes.push({ type: "unchanged", file });
              }
            } else if (previousFileVersion && currentFileVersion) {
              // File existed in both versions, check if it was actually modified
              const hasActualChanges = hasFileChanged(currentFileVersion, previousFileVersion);
              changes.push({
                type: hasActualChanges ? "modified" : "unchanged",
                file,
                previousFile: previousFileVersion,
              });
            } else {
              // No clear state, mark as unchanged
              changes.push({ type: "unchanged", file });
            }
          } catch (error) {
            console.error(`Error fetching history for file ${file.id}:`, error);
            // If we can't get history, mark as unchanged
            changes.push({ type: "unchanged", file });
          }
        }

        setFileChanges(changes);
      } catch (error) {
        console.error("Error fetching files:", error);
        setFileChanges([]);
      } finally {
        setIsFetching(false);
      }
    };

    void fetchFilesAndVersions();
  }, [
    clientId,
    encounterId,
    medplum,
    currentVersionIndex,
    isPatient,
    encounterVersionPairKey,
    currentEncounterQuestionnaireResponse,
    previousEncounterQuestionnaireResponse,
  ]);

  if (isFetching) return <Loading />;

  // Filter changes based on showUnchanged setting
  const filteredChanges = showUnchanged
    ? fileChanges
    : fileChanges.filter((change) => change.type !== "unchanged");

  if (fileChanges.length === 0) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Files
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No files have been uploaded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf")) return <IconFileText size={20} />;
    if (fileType.includes("image")) return <IconFile size={20} />;
    return <IconFileX size={20} />;
  };

  const getFileTypeDisplay = (fileType: string) => {
    if (fileType.includes("pdf")) return "PDF";
    if (fileType.includes("image")) return "Image";
    if (fileType.includes("text")) return "Text";
    if (fileType.includes("word")) return "Word Document";
    return "File";
  };

  const getChangeIcon = (type: string) => {
    switch (type) {
      case "added":
        return <IconPlus size={16} color="green" />;
      case "modified":
        return <IconEdit size={16} color="orange" />;
      case "unchanged":
        return <IconFile size={16} color="gray" />;
      default:
        return null;
    }
  };

  const getChangeColor = (type: string) => {
    switch (type) {
      case "added":
        return "green";
      case "modified":
        return "orange";
      case "unchanged":
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <Paper p="lg" withBorder>
      <Group mb="lg" justify="space-between">
        <Title order={2}>Files</Title>
        <Checkbox
          checked={showUnchanged}
          label="Show unchanged files"
          onChange={(event) => setShowUnchanged(event.currentTarget.checked)}
        />
      </Group>
      <Stack gap="md">
        {filteredChanges.map((change) => (
          <Paper p="md" withBorder key={change.file.id}>
            <Stack mb="lg">
              <Group gap="sm">
                {getFileIcon(change.file.content?.[0]?.attachment?.contentType || "")}
                {getChangeIcon(change.type)}
                <TextWrapper fw={600}>
                  {change.file.identifier?.[0]?.value || "Untitled File"}
                </TextWrapper>
                <TextWrapper fw={600} size="sm" c={getChangeColor(change.type)}>
                  {change.type.toUpperCase()}
                </TextWrapper>
              </Group>
              <TextWrapper size="sm" c="gray.6">
                {getFileTypeDisplay(change.file.content?.[0]?.attachment?.contentType || "")}
              </TextWrapper>
            </Stack>

            {/* Side-by-side comparison for modified files */}
            {change.type === "modified" && change.previousFile && (
              <Group grow align="stretch">
                <Paper p="md" withBorder>
                  <Stack>
                    <Title mb="lg" order={4} c="gray.6">
                      Previous
                    </Title>
                    <Stack gap="md">
                      <Stack gap="0">
                        <TextWrapper fw={600}>File Name</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={change.file.identifier?.[0]?.value || ""}
                          previous={change.previousFile.identifier?.[0]?.value || ""}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Description</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={change.file.description || ""}
                          previous={change.previousFile.description || ""}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>File Type</TextWrapper>
                        <WordsDiff
                          wholeValue
                          showRemoved
                          current={getDocumentTypeDisplay(change.file)}
                          previous={getDocumentTypeDisplay(change.previousFile)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Visibility</TextWrapper>
                        <WordsDiff
                          wholeValue
                          showRemoved
                          current={getVisibilityText(change.file)}
                          previous={getVisibilityText(change.previousFile)}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>
                <Paper p="md" withBorder>
                  <Stack>
                    <Title mb="lg" order={4} c="gray.6">
                      Amended
                    </Title>
                    <Stack gap="md">
                      <Stack gap="0">
                        <TextWrapper fw={600}>File Name</TextWrapper>
                        <WordsDiff
                          current={change.file.identifier?.[0]?.value || ""}
                          previous={change.previousFile.identifier?.[0]?.value || ""}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Description</TextWrapper>
                        <WordsDiff
                          current={change.file.description || ""}
                          previous={change.previousFile.description || ""}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>File Type</TextWrapper>
                        <WordsDiff
                          wholeValue
                          current={getDocumentTypeDisplay(change.file)}
                          previous={getDocumentTypeDisplay(change.previousFile)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Visibility</TextWrapper>
                        <WordsDiff
                          wholeValue
                          current={getVisibilityText(change.file)}
                          previous={getVisibilityText(change.previousFile)}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>
              </Group>
            )}

            {/* Single column for added and unchanged files */}
            {(change.type === "added" || change.type === "unchanged") && (
              <Stack gap="xs">
                {change.file.description && (
                  <TextWrapper size="sm" c="gray.7">
                    {change.file.description}
                  </TextWrapper>
                )}
                <TextWrapper size="sm" c="gray.6">
                  {getVisibilityText(change.file)}
                </TextWrapper>
                <Group mt="sm" justify="space-between">
                  <TextWrapper size="sm" c="gray.6">
                    {change.file.date
                      ? (() => {
                          const dateObject = new Date(change.file.date);
                          return Number.isNaN(dateObject.getTime())
                            ? "Invalid date"
                            : format(dateObject, "MM/dd/yyyy");
                        })()
                      : "No date"}
                  </TextWrapper>
                  {change.file.author?.[0]?.display && (
                    <TextWrapper size="sm" c="gray.6">
                      {change.file.author[0].display}
                    </TextWrapper>
                  )}
                </Group>
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}
